(() => {
  const MOBILE_FILTER_STORAGE_KEY = 'goldenLeafMobileFiltersOpen';
  const MOBILE_MEDIA_QUERY = '(max-width: 749px)';

  const isMobileFiltersViewport = () => window.matchMedia(MOBILE_MEDIA_QUERY).matches;

  const shouldRestoreMobileFilters = () => {
    try {
      return sessionStorage.getItem(MOBILE_FILTER_STORAGE_KEY) === 'true';
    } catch (error) {
      return false;
    }
  };

  const setShouldRestoreMobileFilters = (value) => {
    try {
      if (value) {
        sessionStorage.setItem(MOBILE_FILTER_STORAGE_KEY, 'true');
      } else {
        sessionStorage.removeItem(MOBILE_FILTER_STORAGE_KEY);
      }
    } catch (error) {
      // Storage can fail in restricted browser modes. The drawer still works without restore.
    }
  };

  const initFilterGroups = () => {
    const filterGroups = document.querySelectorAll('[data-filter-group]');
    if (!filterGroups.length) return;

    filterGroups.forEach((group) => {
      const toggle = group.querySelector('[data-filter-toggle]');
      const search = group.querySelector('[data-filter-search]');
      const options = Array.from(group.querySelectorAll('[data-filter-option]'));
      const noResults = group.querySelector('[data-filter-no-results]');

      if (!options.length) return;

      const updateOptions = () => {
        const isExpanded = toggle ? toggle.getAttribute('aria-expanded') === 'true' : true;
        const query = search ? search.value.trim().toLowerCase() : '';
        let visibleCount = 0;

        options.forEach((option) => {
          const label = option.dataset.filterLabel || '';
          const isExtra = option.dataset.filterExtra === 'true';
          const matchesSearch = query === '' || label.includes(query);

          let shouldShow = matchesSearch;

          if (query === '' && isExtra && !isExpanded) {
            shouldShow = false;
          }

          option.hidden = !shouldShow;

          if (shouldShow) visibleCount += 1;
        });

        if (toggle) {
          toggle.hidden = query !== '';
          toggle.textContent = isExpanded
            ? toggle.dataset.showLessLabel || ''
            : toggle.dataset.showMoreLabel || '';
        }

        if (noResults) {
          noResults.hidden = query === '' || visibleCount > 0;
        }
      };

      if (toggle) {
        toggle.addEventListener('click', () => {
          const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
          toggle.setAttribute('aria-expanded', String(!isExpanded));
          group.classList.toggle('is-expanded', !isExpanded);
          updateOptions();
        });
      }

      if (search) {
        search.addEventListener('input', updateOptions);
      }

      updateOptions();
    });
  };

  const initMobileFilterDrawer = () => {
    const filterContainers = document.querySelectorAll('[data-faceted-filters]');
    if (!filterContainers.length) return;

    filterContainers.forEach((container) => {
      const openButton = container.querySelector('[data-mobile-filter-open]');
      const closeButtons = container.querySelectorAll('[data-mobile-filter-close]');
      const drawer = container.querySelector('[data-mobile-filter-drawer]');
      const backdrop = container.querySelector('.faceted-filters__mobile-backdrop');
      const form = container.querySelector('[data-faceted-filters-form]');
      const restoreDrawer = shouldRestoreMobileFilters();

      if (!openButton || !drawer || !form) return;

      const focusableSelector = [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])'
      ].join(',');

      const getFocusableElements = () => {
        return Array.from(drawer.querySelectorAll(focusableSelector)).filter((element) => {
          return element.offsetParent !== null;
        });
      };

      const openDrawer = ({ restore = false } = {}) => {
        if (!isMobileFiltersViewport()) return;

        container.classList.add('is-mobile-filter-open');
        document.body.classList.add('mobile-filters-open');
        openButton.setAttribute('aria-expanded', 'true');

        if (backdrop) {
          backdrop.hidden = false;
        }

        if (!restore) {
          setShouldRestoreMobileFilters(false);
        }

        window.requestAnimationFrame(() => {
          const focusableElements = getFocusableElements();
          const firstFocusable = focusableElements[0];

          if (firstFocusable) {
            firstFocusable.focus({ preventScroll: true });
          }
        });
      };

      const closeDrawer = () => {
        container.classList.remove('is-mobile-filter-open');
        document.body.classList.remove('mobile-filters-open');
        openButton.setAttribute('aria-expanded', 'false');

        if (backdrop) {
          backdrop.hidden = true;
        }

        setShouldRestoreMobileFilters(false);
        openButton.focus({ preventScroll: true });
      };

      const rememberDrawerIfOpen = () => {
        if (container.classList.contains('is-mobile-filter-open') && isMobileFiltersViewport()) {
          setShouldRestoreMobileFilters(true);
        }
      };

      openButton.addEventListener('click', () => {
        openDrawer();
      });

      closeButtons.forEach((button) => {
        button.addEventListener('click', () => {
          closeDrawer();
        });
      });

      form.addEventListener(
        'change',
        () => {
          rememberDrawerIfOpen();
        },
        true
      );

      form.addEventListener('submit', () => {
        rememberDrawerIfOpen();
      });

      form.querySelectorAll('.faceted-filters__remove').forEach((link) => {
        link.addEventListener('click', () => {
          rememberDrawerIfOpen();
        });
      });

      drawer.addEventListener('keydown', (event) => {
        if (!isMobileFiltersViewport() || !container.classList.contains('is-mobile-filter-open')) {
          return;
        }

        if (event.key === 'Escape') {
          event.preventDefault();
          closeDrawer();
          return;
        }

        if (event.key !== 'Tab') return;

        const focusableElements = getFocusableElements();
        if (!focusableElements.length) return;

        const firstFocusable = focusableElements[0];
        const lastFocusable = focusableElements[focusableElements.length - 1];

        if (event.shiftKey && document.activeElement === firstFocusable) {
          event.preventDefault();
          lastFocusable.focus();
        } else if (!event.shiftKey && document.activeElement === lastFocusable) {
          event.preventDefault();
          firstFocusable.focus();
        }
      });

      window.addEventListener('resize', () => {
        if (!isMobileFiltersViewport() && container.classList.contains('is-mobile-filter-open')) {
          closeDrawer();
        }
      });

      if (restoreDrawer && isMobileFiltersViewport()) {
        openDrawer({ restore: true });
      }
    });
  };

  initFilterGroups();
  initMobileFilterDrawer();
})();
